import { EditOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { TextField } from '@refinedev/antd';
import { Button, Flex, Form, Input, Modal, Radio } from 'antd';
import { SelectProps } from 'antd/lib';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router';

import AntdSelect from '@/components/general/AntdSelect/AntdSelect';
import CompanyContactInfoModal from '@/components/general/CompanyContactInfoModal';
import LinkTextField from '@/components/general/LinkTextField';
import { COMPANY_URLS } from '@/constants/urls';
import { usePermissionContext } from '@/contexts/PermissionContext';
import Company from '@/model/General/Company';
import { PACKAGE_STATES } from '@/model/Logistic/PackageStates';
import { Reception } from '@/model/Logistic/Reception/Reception';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';
import { RESOURCES } from '@/Resources';

interface ReceptionShowDetailProps {
  record?: Reception;
  ticket?: any;
  refetch?: () => void;
}

const ReceptionShowDetail = (props: ReceptionShowDetailProps) => {
  const { t } = useTranslation();
  const { receptionRepository } = useRepositoriesContext();
  const { hasPermission } = usePermissionContext();
  const { record, ticket } = props;
  const [openModal, setOpenModal] = useState<{ tracking: boolean; shipper: boolean }>({
    tracking: false,
    shipper: false,
  });
  const closeModal = () => setOpenModal({ tracking: false, shipper: false });
  const [updatingPickUp, setUpdatingPickUp] = useState(false);

  return (
    <div className="ReceptionShowDataCard">
      <div className="ReceptionShowData">
        <b>{t('Empresa o Técnico que envía el paquete')}</b>
        <Flex gap={4}>
          <LinkTextField
            label={`${record?.company?.full_name} (${record?.company?.id})`}
            to={`${COMPANY_URLS.ADMIN_EMPRESA}/${record?.company?.id}`}
            admin
          />
          {record?.company && (
            <CompanyContactInfoModal
              company={new Company(record?.company)}
              openButton={
                <Button
                  className="ReceptionShowInfoButton"
                  type="text"
                  size={'small'}
                  icon={<InfoCircleOutlined />}
                />
              }
            />
          )}
        </Flex>
      </div>

      <div className="ReceptionShowData">
        <Flex gap={6}>
          <b>{t('Operador logístico')}</b>
          {hasPermission('edit') && record?.status == PACKAGE_STATES.PENDIENTE && (
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={() => {
                setOpenModal({ shipper: true, tracking: false });
              }}
            />
          )}
        </Flex>
        <TextField value={record?.shipper?.name ?? '-'} />
      </div>
      <div className="ReceptionShowData">
        <Flex gap={6}>
          <b>{t('Número de seguimiento')}</b>
          {hasPermission('edit') &&
            record?.status != PACKAGE_STATES.FINALIZADO &&
            record?.status != PACKAGE_STATES.CANCELADO && (
              <Button
                icon={<EditOutlined />}
                size="small"
                onClick={() => setOpenModal({ tracking: true, shipper: false })}
              />
            )}
        </Flex>

        <TextField value={record?.tracking_number ?? '-'} />
        {record?.tracking_number && record?.shipper?.tracking_link && (
          <LinkTextField
            to={`${record?.shipper?.tracking_link}/${record?.tracking_number}`}
            label={t('Ver seguimiento')}
            external
          />
        )}
      </div>
      <div className="ReceptionShowData">
        <b>{t('Retiro en deposito')}:</b>
        {hasPermission('edit') &&
        (record?.status == PACKAGE_STATES.PENDIENTE ||
          record?.status == PACKAGE_STATES.EN_VIAJE) ? (
          <Radio.Group
            value={
              record?.has_to_be_picked_up == undefined ? undefined : record?.has_to_be_picked_up
            }
            onChange={e => {
              setUpdatingPickUp(true);
              void receptionRepository
                .updateReception(record.id, {
                  has_to_be_picked_up: e.target.value,
                })
                .then(() => {
                  props.refetch?.();
                  setUpdatingPickUp(false);
                });
            }}
            disabled={updatingPickUp}>
            <Radio value={true}>{t('Sí')}</Radio>
            <Radio value={false}>{t('No')}</Radio>
          </Radio.Group>
        ) : (
          <TextField value={record?.has_to_be_picked_up ? t('Sí') : t('No')} />
        )}
      </div>
      {record?.ticket && ticket && (
        <div className="ReceptionShowData">
          <b>{t('Ticket asociado')}</b>
          <LinkTextField
            label={`${ticket?.ticket_title_str} (${ticket?.id})`}
            to={`/tickets/show/${ticket?.id}`}
            local
          />
          <LinkTextField
            label={t('Ver caso completo')}
            to={`/${RESOURCES.CASE_RESOLUTION}/${ticket?.id}`}
            local
          />
        </div>
      )}
      <Modal
        open={openModal.tracking || openModal.shipper}
        onCancel={closeModal}
        footer={null}
        title={
          openModal.tracking ? t('Editar número de seguimiento') : t('Editar operador logístico')
        }>
        {openModal.tracking ? (
          <UpdateTrackingNumberModal
            record={record!}
            closeModal={closeModal}
            refetch={props.refetch}
          />
        ) : (
          <UpdateShipperModal record={record!} closeModal={closeModal} refetch={props.refetch} />
        )}
      </Modal>
    </div>
  );
};

interface UpdateModalProps {
  record: Reception;
  closeModal: () => void;
  refetch?: () => void;
}

const UpdateTrackingNumberModal = (props: UpdateModalProps) => {
  const { t } = useTranslation();
  const { receptionRepository } = useRepositoriesContext();
  const { record, closeModal, refetch } = props;
  const [newTrackingNumber, setNewTrackingNumber] = useState(record?.tracking_number);

  useEffect(() => {
    setNewTrackingNumber(record?.tracking_number);
  }, [record?.tracking_number]);

  const onConfirmTrackingChange = useCallback(() => {
    if (!record?.id) return;

    if (newTrackingNumber === record?.tracking_number) {
      closeModal();

      return;
    }

    void receptionRepository
      .updateReception(record.id, { tracking_number: newTrackingNumber || '' })
      .then(() => {
        closeModal();
        refetch?.();
      });
  }, [record, newTrackingNumber, receptionRepository, closeModal, refetch]);

  return (
    <div className="UpdateTrackingNumberModal">
      <Form onFinish={onConfirmTrackingChange} layout="vertical">
        <Form.Item>
          <Input
            defaultValue={record?.tracking_number}
            value={newTrackingNumber}
            onChange={e => setNewTrackingNumber(e.target.value)}
          />
        </Form.Item>
        <Flex justify="end" gap={8}>
          <Button onClick={closeModal}>{t('Cancelar')}</Button>
          <Button type="primary" htmlType="submit">
            {t('Guardar')}
          </Button>
        </Flex>
      </Form>
    </div>
  );
};

const UpdateShipperModal = (props: UpdateModalProps) => {
  const { t } = useTranslation();
  const { receptionRepository } = useRepositoriesContext();
  const { record, closeModal, refetch } = props;
  const { shippersSelectProps } = useOutletContext<{
    shippersSelectProps: SelectProps;
  }>();
  const [newShipperId, setNewShipperId] = useState<number | undefined>(record?.shipper?.id);
  const onConfirmShipperChange = useCallback(() => {
    if (!record?.id || !newShipperId) return;

    if (newShipperId === record?.shipper?.id) {
      closeModal();

      return;
    }

    void receptionRepository.updateReceptionShipper(record.id, newShipperId).then(() => {
      closeModal();
      refetch?.();
    });
  }, [closeModal, newShipperId, receptionRepository, record, refetch]);

  return (
    <div className="UpdateShipperModal">
      <Form layout="vertical" onFinish={onConfirmShipperChange}>
        <Form.Item label={t('Operador logístico')} required={true}>
          <AntdSelect
            {...shippersSelectProps}
            allowClear
            showSearch
            value={newShipperId}
            onChange={value => setNewShipperId(value as number | undefined)}
          />
        </Form.Item>
        <Flex justify="end" gap={8}>
          <Button onClick={closeModal}>{t('Cancelar')}</Button>
          <Button type="primary" htmlType="submit">
            {t('Guardar')}
          </Button>
        </Flex>
      </Form>
    </div>
  );
};

export default ReceptionShowDetail;
